import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, X, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  enablePushNotifications,
  isIOS,
  isRunningStandalone,
  markPromptDismissed,
  shouldShowPushPrompt,
} from "@/lib/push";

/**
 * Bottom-of-screen prompt shown to logged-in parents (and staff, optionally)
 * asking them to enable push notifications.
 *
 * Two modes:
 *  - Standard browsers (Chrome/Edge/Firefox/Android): "Enable Notifications"
 *    button triggers the browser permission flow directly.
 *  - iOS Safari NOT installed as PWA: shows a 3-step "Add to Home Screen"
 *    instruction since iOS won't grant push permission unless installed first.
 */
export default function PushPromptBanner() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("push"); // "push" | "ios_install"

  useEffect(() => {
    // Wait a moment so we don't nag the user immediately on login
    const t = setTimeout(() => {
      if (!shouldShowPushPrompt()) return;
      if (isIOS() && !isRunningStandalone()) {
        setMode("ios_install");
      } else {
        setMode("push");
      }
      setVisible(true);
    }, 3500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    markPromptDismissed();
    setVisible(false);
  };

  const handleEnable = async () => {
    setBusy(true);
    try {
      const res = await enablePushNotifications();
      if (res.ok) {
        toast.success("Notifications enabled — you'll get instant class updates.");
        setVisible(false);
      } else if (res.reason === "denied") {
        toast.error("Notifications blocked. You can re-enable them in your browser settings.");
        setVisible(false);
      } else if (res.reason === "server_not_configured") {
        toast.error("Push notifications are not configured on this server yet.");
        setVisible(false);
      } else {
        toast.error(`Could not enable notifications (${res.reason || "unknown"})`);
      }
    } catch (e) {
      toast.error(e?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  if (mode === "ios_install") {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-40
                   rounded-xl border-2 border-secondary/60 bg-white shadow-xl p-4"
        data-testid="push-prompt-ios-install"
      >
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
          data-testid="push-prompt-close-btn"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 shrink-0 rounded-full bg-secondary/15 flex items-center justify-center">
            <Bell className="h-5 w-5 text-secondary" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-sm">Install Cornerstone Music School</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add this app to your Home Screen for instant class updates & faster access.
            </p>
            <ol className="text-xs mt-3 space-y-1.5">
              <li className="flex items-center gap-1.5">
                <span className="font-semibold">1.</span> Tap <Share className="h-3.5 w-3.5 inline mx-0.5" /> (Share in Safari)
              </li>
              <li className="flex items-center gap-1.5">
                <span className="font-semibold">2.</span> Tap <Plus className="h-3.5 w-3.5 inline mx-0.5" /> &quot;Add to Home Screen&quot;
              </li>
              <li>
                <span className="font-semibold">3.</span> Tap &quot;Add&quot; in the top-right
              </li>
            </ol>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={dismiss}
                      data-testid="push-prompt-later-btn">
                Remind me later
              </Button>
              <Button size="sm" onClick={dismiss} data-testid="push-prompt-got-it-btn">
                Got it
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-40
                 rounded-xl border-2 border-secondary/60 bg-white shadow-xl p-4"
      data-testid="push-prompt-standard"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
        data-testid="push-prompt-close-btn"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 rounded-full bg-secondary/15 flex items-center justify-center">
          <Bell className="h-5 w-5 text-secondary" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm">Enable instant class updates?</p>
          <p className="text-xs text-muted-foreground mt-1">
            Get notified about last-minute cancellations, replacement classes, and payment
            reminders on your phone&apos;s lock screen.
          </p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleEnable} disabled={busy}
                    data-testid="push-prompt-enable-btn">
              {busy ? "Requesting…" : "Enable notifications"}
            </Button>
            <Button variant="outline" size="sm" onClick={dismiss}
                    data-testid="push-prompt-not-now-btn">
              Not now
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            You can change this anytime in your browser settings.
          </p>
        </div>
      </div>
    </div>
  );
}
